'use client';

import { FC, useEffect } from 'react';
import { getAppConfig } from '@bts-web/core-app-config';
import { useFeatureFlag } from '../../../../statsig/sdk/provider';
import { getAssetTradeUrl } from '../../../../route-builders';
import { Z_INDEX_LIBRARY, css } from '@bts-web/utils-style-engine';
import {
  RegularBuy,
  RegularSell,
  RegularClockPlusOutlineAlt,
} from '../../../../common/svgIcons';
import { HyperLinkButtonBase } from '../../../../common/components/HyperLinkButtonBase/HyperLinkButtonBase';
import { useAppNotification } from '../../../../notifications/NotificationContextProvider';
import { MarketStatusBanner } from './MarketClosedBanner';
import { AssetDetailsCTAMoreButton } from './AssetDetailsCTAMoreButton';
import {
  useUserAccessControl,
  UserAccessWithRoleCheck,
} from '../../../../common/utils/userAccess';
import { useIsBelowScreenWidth } from '../../../../layouts/utils/useIsBelowScreenWidth';
import { useClientUserInfo } from '../../../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';

interface AssetDetailsFloatingCTAProps {
  isOwningAsset: boolean;
  isAssetActive: boolean;
  isBuyActive: boolean;
  isSellActive: boolean;
  isMarketOpen: boolean;
  assetId: string;
  locale: string;
  marketClosedUntil?: Date;
  marketClosingAt?: Date;
  translations: {
    buy: string;
    sell: string;
    savingsPlan: string;
    errorAssetNotAvailableTitle: string;
    errorAssetNotAvailableSubtitle: string;
  };
}

const inlineButtonClass = css({
  flex: 1,
  textAlign: 'center',
});

const AssetDetailsFloatingCTASpacer = () => (
  <div
    aria-label="floating menu spacer"
    className={css({ height: '125px', minHeight: '125px', maxHeight: '125px' })}
  />
);

const AssetDetailsFloatingCTA: FC<AssetDetailsFloatingCTAProps> = ({
  isOwningAsset,
  isAssetActive,
  isBuyActive,
  isSellActive,
  isMarketOpen,
  assetId,
  locale,
  marketClosedUntil,
  marketClosingAt,
  translations,
}) => {
  const { setAppNotification, closeNotificationById } = useAppNotification();

  const { isIslamicUser } = useClientUserInfo();

  const { appId } = getAppConfig();

  const feature_hasSwapTradeEnabled = useFeatureFlag(
    'feature_hasSwapTradeEnabled',
  );

  const feature_hasCryptoTransfersEnabled = useFeatureFlag(
    'feature_hasCryptoTransfersEnabled',
  );

  const feature_hasSavingsPlansEnabled = useFeatureFlag(
    'feature_hasSavingsPlansEnabled',
  );

  const feature_assetDetailsMarketClosedBanner = useFeatureFlag(
    'feature_assetDetailsMarketClosedBanner',
  );

  const { isPepFailed } = useUserAccessControl();

  const INACTIVE_ASSET_NOTIFICATION_ID = `asset-inactive-${assetId}`;

  useIsBelowScreenWidth(1024, (isMobile) => {
    if (!isAssetActive) {
      if (isMobile) {
        setAppNotification({
          id: INACTIVE_ASSET_NOTIFICATION_ID,
          title: translations.errorAssetNotAvailableTitle,
          subtitle: translations.errorAssetNotAvailableSubtitle,
          visual: 'info',
          customDuration: 'infinite',
          withClose: true,
        });
      }
    }
  });

  useEffect(() => {
    return () => {
      closeNotificationById(INACTIVE_ASSET_NOTIFICATION_ID);
    };
  }, []);

  const isBuyDisabled = !isBuyActive || !isAssetActive || isPepFailed;

  const isSellDisabled =
    !isSellActive || !isAssetActive || isPepFailed || !isOwningAsset;

  const showMarketClosedBanner =
    feature_assetDetailsMarketClosedBanner &&
    (marketClosedUntil || marketClosingAt);

  return (
    <div
      role="menu"
      aria-label="trading options menu"
      style={{
        zIndex: Z_INDEX_LIBRARY.MOBILE_NAVIGATION,
      }}
      className={css({
        display: 'flex',
        p: 'small',
        position: 'fixed',
        bottom: 0,
        insetInline: 0,
      })}
    >
      <div
        data-testid="asset-details-mobile-trade-options"
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 'small',
          p: 'small',
          position: 'fixed',
          bottom: 0,
          insetInline: 0,
          backgroundColor: 'screen_background.primary',
          boxShadow: `0px -6px 12px 0px rgba(158,158,158,0.20)`,
        })}
      >
        {showMarketClosedBanner && (
          <MarketStatusBanner
            marketClosedUntil={marketClosedUntil}
            marketClosingAt={marketClosingAt}
            locale={locale}
          />
        )}

        <div
          className={css({
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 'extra_small',
          })}
        >
          <UserAccessWithRoleCheck>
            <HyperLinkButtonBase
              disabled={isBuyDisabled || isMarketOpen === false}
              size="medium"
              visual={appId === 4 || isIslamicUser ? 'accent' : 'primary'}
              to={getAssetTradeUrl(assetId, {
                type: 'buy',
                trailingSlash: true,
              })}
              className={inlineButtonClass}
              aria-label={`${translations.buy} ${isAssetActive ? '' : '(unavailable)'}`}
            >
              {translations.buy}{' '}
              <RegularBuy color="neutrals.text_primary_inverted" />
            </HyperLinkButtonBase>
          </UserAccessWithRoleCheck>

          {feature_hasSavingsPlansEnabled && !isIslamicUser ? (
            <UserAccessWithRoleCheck>
              <HyperLinkButtonBase
                disabled={!isAssetActive || isPepFailed}
                size="medium"
                visual="secondary"
                to={getAssetTradeUrl(assetId, {
                  type: 'savings',
                  trailingSlash: true,
                })}
                className={inlineButtonClass}
                inverse
                aria-label={translations.savingsPlan}
              >
                {translations.savingsPlan} <RegularClockPlusOutlineAlt />
              </HyperLinkButtonBase>
            </UserAccessWithRoleCheck>
          ) : (
            isOwningAsset && (
              <UserAccessWithRoleCheck>
                <HyperLinkButtonBase
                  disabled={isSellDisabled}
                  size="medium"
                  visual="secondary"
                  to={getAssetTradeUrl(assetId, {
                    type: 'sell',
                    trailingSlash: true,
                  })}
                  className={inlineButtonClass}
                  inverse
                  aria-label={`${translations.sell} ${!isOwningAsset ? '(no holdings)' : isAssetActive ? '' : '(unavailable)'}`}
                >
                  {translations.sell} <RegularSell />
                </HyperLinkButtonBase>
              </UserAccessWithRoleCheck>
            )
          )}

          {((feature_hasSwapTradeEnabled && !isIslamicUser) ||
            feature_hasCryptoTransfersEnabled ||
            (feature_hasSavingsPlansEnabled && !isIslamicUser)) && (
            <AssetDetailsCTAMoreButton
              assetId={assetId}
              isBuyDisabled={isBuyDisabled}
              isSellDisabled={isSellDisabled}
              isOwningAsset={isOwningAsset}
              isMarketOpen={isMarketOpen}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export { AssetDetailsFloatingCTA, AssetDetailsFloatingCTASpacer };
